// 용지(page) 물리 치수 — size + orientation → px/mm 산출(단일 소스).
//
// 엔진(RenderTemplate)·편집 캔버스(admin)·페이지 분할(pagePlan)이 모두 이 헬퍼로
// 폭·높이를 산출해야 캔버스 박스와 낱장 분할이 어긋나지 않는다.
// orientation='landscape'면 세로 기준 치수의 W/H를 swap한다(A4 210×297 → 297×210mm).
// orientation 미지정(undefined)=portrait(세로) — 기존 템플릿 하위호환.

import type { PageSpec, PageSize } from '@safetyhub/shared';

/** 용지 크기별 세로(portrait) 기준 px 치수(96dpi). RenderTemplate 폭 A4=794px와 정합. */
export const PAGE_PX_PORTRAIT: Record<PageSize, { w: number; h: number }> = {
  A4: { w: 794, h: 1123 },
  A3: { w: 1123, h: 1587 },
  LETTER: { w: 816, h: 1056 },
};

/** 용지 크기별 세로(portrait) 기준 mm 치수(표시용). */
export const PAGE_MM_PORTRAIT: Record<PageSize, { w: number; h: number }> = {
  A4: { w: 210, h: 297 },
  A3: { w: 297, h: 420 },
  LETTER: { w: 215.9, h: 279.4 },
};

type PageDim = Pick<PageSpec, 'size' | 'orientation'>;

/** page(size+orientation) → px 치수. landscape면 W/H swap. 미지정=portrait. */
export function pagePx(page: PageDim): { w: number; h: number } {
  const base = PAGE_PX_PORTRAIT[page.size] ?? PAGE_PX_PORTRAIT.A4;
  return page.orientation === 'landscape' ? { w: base.h, h: base.w } : { ...base };
}

/** page(size+orientation) → mm 치수(표시용). landscape면 W/H swap. 미지정=portrait. */
export function pageMm(page: PageDim): { w: number; h: number } {
  const base = PAGE_MM_PORTRAIT[page.size] ?? PAGE_MM_PORTRAIT.A4;
  return page.orientation === 'landscape' ? { w: base.h, h: base.w } : { ...base };
}

/**
 * 한 낱장이 **본문에 쓸 수 있는 세로 높이**(px) = 용지 높이 − 위아래 여백. 최소 1(0/음수 여백
 * 조합에서 분할 루프가 전진하지 못하는 것을 막는다).
 *
 * 페이지 분할(`planEditPages`)의 예산이자 오버사이즈 사전 판정(`findOversizeUnits`)의 기준선이라,
 * 두 곳이 같은 값을 봐야 "캔버스는 넘치는데 경고는 없다"가 생기지 않는다.
 */
export function pageContentHeight(page: PageSpec): number {
  return Math.max(1, pagePx(page).h - page.margins.top - page.margins.bottom);
}
